import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { useSelectionCountContext } from 'core/states';
import { GetManyResult, GetOneResult, IInfiniteQueryOptions, IRequestPayload } from 'core/types';
import { jobService } from 'jobs/services';
import {
  IJob,
  IJobCustomer,
  IJobFile,
  IJobRequestPayload,
  IJobVendor,
  IUpsertJobCustomerPayload,
  IUpsertJobVendorPayload,
} from 'jobs/types';

export const useGetJob = (criteria: IJobRequestPayload, enabled = true): GetOneResult<IJob> => {
  const { data, isLoading, isError } = useQuery(
    ['job', criteria],
    ({ signal }) => jobService.getJob(criteria, signal),
    { enabled }
  );

  return {
    record: data?.record ?? null,
    isLoading,
    isError,
  };
};

export const useGetJobStatuses = () => {
  const { data, isLoading, isFetching, isError } = useQuery(
    ['jobStatuses'],
    ({ signal }) => jobService.getJobStatuses(signal),
    { enabled: true }
  );

  return {
    records: data?.records ?? [],
    count: data?.count ?? 0,
    isLoading,
    isFetching,
    isError,
  };
};

export const useGetJobFiles = (
  requestPayload: { filters: { bomId: number } },
  options: { enabled?: boolean } = {}
): GetManyResult<IJobFile> => {
  const { data, isLoading } = useQuery(
    ['jobFiles', requestPayload],
    ({ signal }) => jobService.getJobFiles(requestPayload, signal),
    {
      keepPreviousData: false,
      ...options,
    }
  );

  return {
    records: data?.records ?? [],
    isLoading,
  };
};

export const useGetJobCustomers = (
  requestPayload?: IRequestPayload,
  options: IInfiniteQueryOptions<IJobCustomer> = { fetchAllPages: true }
) =>
  useInfiniteQuery<IJobCustomer>(
    jobService.getJobCustomers,
    'jobCustomers',
    requestPayload,
    options
  );

export const useGetJobVendors = (
  requestPayload?: IRequestPayload,
  options: IInfiniteQueryOptions<IJobVendor> = { fetchAllPages: true }
) => useInfiniteQuery<IJobVendor>(jobService.getJobVendors, 'jobVendors', requestPayload, options);

export const useJobActions = () => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useUpsertJob(),
    ...useUpsertJobs(selectionCount),
    ...useUpdateJobName(),
    ...useExportJobLog(),
    ...useAttachJobFile(),
    ...useDetachJobFile(),
  };
};

const useUpsertJob = () => {
  const { errors } = useHandleErrors({
    success: ({ bom }) =>
      bom.id
        ? { message: t('messageJobUpdateSuccess', { count: 1 }) }
        : { message: t('messageJobCreateSuccess') },
    loading: ({ bom }) =>
      bom.id
        ? { message: t('messageJobUpdateInProgress', { count: 1 }) }
        : { message: t('messageJobCreateInProgress') },
    error: ({ bom }) =>
      bom.id
        ? { title: t('errorTitleJobUpdate', { count: 1 }) }
        : { title: t('errorTitleJobCreate') },
    position: NotificationPosition.FORM,
    invalidateQueries: ['job'],
  });

  const { mutateAsync } = useMutation(jobService.upsertJob, {
    ...errors,
  });

  return { upsertJob: mutateAsync };
};

const useUpsertJobs = (count: number) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageJobUpdateSuccess', { count }) },
    loading: { message: t('messageJobUpdateInProgress', { count }) },
    error: { title: t('errorTitleJobUpdate', { count }) },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobService.upsertJobs, {
    ...errors,
  });

  return { upsertJobs: mutateAsync };
};

const useUpdateJobName = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageJobNameUpdateSuccess') },
    loading: { message: t('messageJobNameUpdateInProgress') },
    error: { title: t('errorTitleJobNameUpdating') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(jobService.renameJob, {
    ...errors,
  });

  return { updateJobName: mutateAsync };
};

const useExportJobLog = () => {
  const { errors } = useHandleErrors({
    error: { title: t('errorExportingJobLog') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(jobService.exportJobLog, {
    ...errors,
  });

  return { exportJobLog: mutateAsync };
};

const useAttachJobFile = () => {
  const { errors } = useHandleErrors({
    loading: { message: t('messageJobFileAttachInProgress') },
    success: { message: t('messageJobFileAttachSuccess') },
    error: { title: t('errorTitleJobFileAttach') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['jobFiles'],
  });

  const { mutateAsync } = useMutation(jobService.attachJobFile, {
    ...errors,
  });

  return { attachJobFile: mutateAsync };
};

const useDetachJobFile = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageJobFileDetachSuccess') },
    loading: { message: t('messageJobFileDetachInProgress') },
    error: { title: t('errorTitleJobFileDetach') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['jobFiles'],
  });

  const { mutateAsync } = useMutation(jobService.detachJobFile, {
    ...errors,
  });

  return { detachJobFile: mutateAsync };
};

export const useJobCustomerActions = (
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => {
  return {
    ...useUpsertJobCustomer(notificationPosition),
    ...useRemoveJobCustomers(notificationPosition),
  };
};

const useUpsertJobCustomer = (notificationPosition: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (payload: IUpsertJobCustomerPayload) =>
      payload.bomCustomerCompanyAssociation.id
        ? { message: t('messageCustomerUpdatedSuccess') }
        : { message: t('messageCustomerCreatedSuccess') },
    loading: (payload: IUpsertJobCustomerPayload) =>
      payload.bomCustomerCompanyAssociation.id
        ? { message: t('messageCustomerUpdateInProgress') }
        : { message: t('messageCustomerCreateInProgress') },
    error: (payload: IUpsertJobCustomerPayload) =>
      payload.bomCustomerCompanyAssociation.id
        ? { title: t('errorTitleCustomerUpdate') }
        : { title: t('errorTitleCustomerCreate') },
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(jobService.upsertJobCustomer, {
    ...errors,
  });

  return { upsertJobCustomer: mutateAsync };
};

const useRemoveJobCustomers = (notificationPosition: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (ids: number[]) => ({
      message: t('messageCustomersRemovedSuccess', { count: ids.length }),
    }),
    loading: (ids: number[]) => ({
      message: t('messageCustomersRemoveInProgress', { count: ids.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitleCustomersRemove', { count: ids.length }),
    }),
    position: notificationPosition,
  });

  const { mutateAsync, isLoading } = useMutation(jobService.removeJobCustomers, {
    ...errors,
  });

  return { removeJobCustomers: mutateAsync, isLoading };
};

export const useJobVendorActions = (
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => {
  return {
    ...useUpsertJobVendor(notificationPosition),
    ...useRemoveJobVendors(notificationPosition),
  };
};

const useUpsertJobVendor = (notificationPosition: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: ({ bomVendorCompanyAssociation }: IUpsertJobVendorPayload) =>
      bomVendorCompanyAssociation.id
        ? { message: t('messageVendorUpdatedSuccess') }
        : { message: t('messageVendorCreatedSuccess') },
    loading: ({ bomVendorCompanyAssociation }: IUpsertJobVendorPayload) =>
      bomVendorCompanyAssociation.id
        ? { message: t('messageVendorUpdateInProgress') }
        : { message: t('messageVendorCreateInProgress') },
    error: ({ bomVendorCompanyAssociation }: IUpsertJobVendorPayload) =>
      bomVendorCompanyAssociation.id
        ? { title: t('errorTitleVendorUpdate') }
        : { title: t('errorTitleVendorCreate') },
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(jobService.upsertJobVendor, {
    ...errors,
  });

  return { upsertJobVendor: mutateAsync };
};

const useRemoveJobVendors = (notificationPosition: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (ids: number[]) => ({
      message: t('messageVendorsRemovedSuccess', { count: ids.length }),
    }),
    loading: (ids: number[]) => ({
      message: t('messageVendorsRemoveInProgress', { count: ids.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitleVendorsRemove', { count: ids.length }),
    }),
    position: notificationPosition,
  });

  const { mutateAsync, isLoading } = useMutation(jobService.removeJobVendors, {
    ...errors,
  });

  return { removeJobVendors: mutateAsync, isLoading };
};
